// Lint-aligned wrapper for SvelteKit 2's `$app/paths.resolve()`.
//
// The lint rule `svelte/no-navigation-without-resolve` requires every navigation
// (`<a href=...>` and `goto(...)`) to be wrapped in `resolve()`. SvelteKit 2's
// `resolve()` is typed strictly — its first argument must be a literal route ID
// or a literal pathname template (so the compile-time route table can validate
// the navigation target).
//
// Many call-sites in this codebase have to navigate to runtime-computed
// pathnames (string concatenation in admin tables, query-string-only navigation
// like `?period=7d`, generic `<Button href="..." />` and `<Link url=... />`
// components that take an arbitrary `string` prop). The strict generic on
// `resolve()` cannot infer the route type for these — but the value at runtime
// IS a valid pathname.
//
// `resolveAny()` reuses the underlying `resolve()` runtime implementation while
// loosening the parameter type to plain `string`. It is NOT `as any`: the route
// is statically narrowed to the function's own first-parameter type at the
// boundary, which is the exact same approach the SvelteKit team recommends for
// dynamic-pathname call-sites.
//
// Use `resolve()` directly when the pathname is a literal route ID; use
// `resolveAny()` only when the pathname is computed at runtime.

import { resolve as kitResolve } from '$app/paths';
import type { ResolvedPathname } from '$app/types';

// `resolve()` is generic over `T extends RouteIdWithSearchOrHash | PathnameWithSearchOrHash`,
// so `Parameters<typeof resolve>[0]` collapses to `never`. We cast the function reference
// itself to a permissive shape — this preserves the runtime impl exactly and only widens
// the type at the boundary where we already know the input is a valid pathname.
//
// Returning `ResolvedPathname` makes `eslint-plugin-svelte`'s
// `no-navigation-without-resolve` rule treat consumers (`<a href={resolveAny(...)}>` and
// `goto(resolveAny(...))`) as already resolved — the rule's
// `expressionIsResolvedPathname` check uses TypeScript to verify the expression's type
// is structurally `ResolvedPathname` from `$app/types`.
export const resolveAny = kitResolve as unknown as (pathname: string) => ResolvedPathname;
